"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.logicOperatorSchema = void 0;
exports.createColumnFilterZodSchema = createColumnFilterZodSchema;
exports.createSortZodSchema = createSortZodSchema;
const zod_1 = require("zod");
const types_1 = require("./types");
exports.logicOperatorSchema = zod_1.z.enum([
    types_1.LOGIC_OPERATORS.AND,
    types_1.LOGIC_OPERATORS.OR,
]);
const NUMERIC_VALUE_OPERATORS = new Set([
    'equals',
    'doesNotEqual',
    'is',
    'not',
    'greaterThan',
    'greaterThanOrEqual',
    'lessThan',
    'lessThanOrEqual',
]);
const DATE_VALUE_OPERATORS = new Set([
    'equals',
    'doesNotEqual',
    'is',
    'not',
    'greaterThan',
    'greaterThanOrEqual',
    'lessThan',
    'lessThanOrEqual',
]);
function isParseableDate(value) {
    const trimmed = value.trim();
    const ms = /^-?\d+$/.test(trimmed) ? Number(trimmed) : Date.parse(trimmed);
    return !Number.isNaN(ms);
}
function refineTypedValue(filter, ctx, valueOperators, isValid, expectation) {
    if (valueOperators.has(filter.operator)) {
        if (filter.value === undefined || filter.value === '') {
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'Filter requires a value', path: ['value'] });
            return;
        }
        if (!isValid(filter.value)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `Filter value is not ${expectation}: ${filter.value}`,
                path: ['value'],
            });
        }
    }
    else if (filter.operator === 'isAnyOf' || filter.operator === 'isNoneOf') {
        if (filter.value === undefined || filter.value === '') {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `${filter.operator} requires a value`,
                path: ['value'],
            });
            return;
        }
        const parts = parseIsAnyOfForValidation(filter.value);
        if (parts.length === 0) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `${filter.operator} contains no valid entries`,
                path: ['value'],
            });
            return;
        }
        for (const part of parts) {
            if (!isValid(part)) {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    message: `${filter.operator} contains an entry that is not ${expectation}: ${part}`,
                    path: ['value'],
                });
            }
        }
    }
}
function createColumnFilterZodSchema(schema, options) {
    const maxFilters = options?.maxFilters ?? 20;
    const maxValueLength = options?.maxValueLength ?? 1000;
    const allowedFields = [...Object.keys(schema.fields), ...(schema.ignoredFields ?? [])];
    const fieldDescription = renderFieldLegend(schema, 'Filter field.');
    const columnFilterItemSchema = zod_1.z
        .object({
        field: zod_1.z.enum(allowedFields).describe(fieldDescription),
        operator: zod_1.z.enum(types_1.COLUMN_FILTER_OPERATORS),
        value: zod_1.z.coerce.string().max(maxValueLength).optional(),
    })
        .superRefine((filter, ctx) => {
        const fieldDef = schema.fields[filter.field];
        if (!fieldDef) {
            return;
        }
        if (fieldDef.options.type === types_1.FieldType.Number) {
            refineTypedValue(filter, ctx, NUMERIC_VALUE_OPERATORS, (v) => Number.isFinite(Number(v)), 'a finite number');
        }
        else if (fieldDef.options.type === types_1.FieldType.Date) {
            refineTypedValue(filter, ctx, DATE_VALUE_OPERATORS, isParseableDate, 'a valid date');
        }
    });
    return zod_1.z
        .array(columnFilterItemSchema)
        .max(maxFilters)
        .transform((filters) => filters.map((f) => {
        const fieldDef = schema.fields[f.field];
        if (!fieldDef || f.value === undefined) {
            return f;
        }
        if ('values' in fieldDef.options && fieldDef.options.values) {
            const dbValue = fieldDef.options.values[f.value];
            if (dbValue !== undefined) {
                return { ...f, value: dbValue };
            }
        }
        return f;
    }));
}
function parseIsAnyOfForValidation(value) {
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return parsed
                .filter((x) => {
                const t = typeof x;
                return t === 'string' || t === 'number' || t === 'boolean';
            })
                .map(String);
        }
        if (typeof parsed === 'string' || typeof parsed === 'number' || typeof parsed === 'boolean') {
            return [String(parsed)];
        }
        return [];
    }
    catch {
        return value
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean);
    }
}
function createSortZodSchema(schema) {
    const sortableFields = Object.keys(schema.fields);
    const fieldDescription = renderFieldLegend(schema, 'Sort field.');
    return zod_1.z.array(zod_1.z.object({
        field: zod_1.z.enum(sortableFields).describe(fieldDescription),
        direction: zod_1.z.enum(['asc', 'desc']),
    }));
}
function renderFieldLegend(schema, prefix) {
    const entries = Object.entries(schema.fields);
    const anyDescribed = entries.some(([, def]) => def.description !== undefined && def.description !== '');
    if (!anyDescribed) {
        return prefix;
    }
    const lines = entries.map(([name, def]) => {
        const desc = def.description;
        return desc ? `- ${name}: ${desc}` : `- ${name}`;
    });
    return `${prefix} Fields:\n${lines.join('\n')}`;
}
//# sourceMappingURL=zod-helpers.js.map